import { useCallback, useMemo, useState } from "react";
import { Sparkles, Shapes, X, RotateCw } from "lucide-react";
import DiagramRenderer from "@/components/hormone/DiagramRenderer";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai`;

/**
 * Render markdown-ish text as paragraphs + bullet lists.
 * We keep it tiny on purpose — no full MD parser needed.
 */
function ExplainBody({ text }) {
  const blocks = text.split(/\n{2,}/);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {blocks.map((b, i) => {
        const lines = b.split("\n").filter(Boolean);
        const allBullets = lines.every((l) => /^[-*]\s+/.test(l));
        if (allBullets) {
          return (
            <ul key={i} style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
              {lines.map((l, j) => (
                <li
                  key={j}
                  style={{
                    fontSize: 15,
                    lineHeight: 1.6,
                    color: "var(--text-2)",
                  }}
                >
                  {renderInline(l.replace(/^[-*]\s+/, ""))}
                </li>
              ))}
            </ul>
          );
        }
        // Heading (markdown-ish **Heading**) — render as paragraph w/ bolded words
        return (
          <p
            key={i}
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.65,
              color: "var(--text-2)",
            }}
          >
            {renderInline(b)}
          </p>
        );
      })}
    </div>
  );
}

function renderInline(s) {
  // Very small **bold** + *italic* parser
  const parts = [];
  let i = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let m;
  let last = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    if (m[2]) parts.push(<strong key={i++} style={{ color: "var(--text)" }}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={i++}>{m[3]}</em>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
}

function Panel({ children, onClose, title, onRetry, loading }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="ai-panel"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "color-mix(in srgb, var(--bg) 70%, transparent)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        className="card rise"
        style={{
          width: "100%",
          maxWidth: 720,
          maxHeight: "86vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 20,
        }}
      >
        <header
          style={{
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            borderBottom: "1px solid var(--border)",
            background: "var(--surface)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="t-meta"
              style={{ color: "var(--accent)", fontSize: 11 }}
            >
              AI assistant · Claude Sonnet 4.5
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 18,
                fontWeight: 500,
                color: "var(--text)",
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </div>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="btn btn-ghost touch"
              data-testid="ai-panel-retry-btn"
              aria-label="Regenerate"
              disabled={loading}
              style={{ padding: "0 12px", minHeight: 40, fontSize: 13 }}
            >
              <RotateCw size={14} strokeWidth={1.75} aria-hidden="true" />
              Regenerate
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost touch"
            data-testid="ai-panel-close-btn"
            aria-label="Close"
            style={{
              width: 44,
              height: 44,
              padding: 0,
              borderRadius: 999,
              minHeight: 44,
            }}
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </header>
        <div style={{ padding: "18px 20px", overflow: "auto" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Skeleton({ lines = 6 }) {
  return (
    <div data-testid="ai-panel-skeleton" style={{ display: "grid", gap: 10 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skeleton"
          style={{ height: 14, width: `${60 + ((i * 13) % 35)}%` }}
        />
      ))}
    </div>
  );
}

async function postJSON(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${t || res.statusText}`);
  }
  return res.json();
}

export default function AiActions({ lesson, moduleTitle }) {
  const [mode, setMode] = useState(null); // "explain" | "diagram" | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [explain, setExplain] = useState(null);
  const [diagram, setDiagram] = useState(null);
  const { toast } = useToast();

  const ctx = useMemo(() => {
    const sectionsText = lesson.content.sections
      .map((s) => `## ${s.heading}\n${s.body || ""}`)
      .join("\n\n");
    return {
      lesson_id: lesson.id,
      lesson_title: lesson.title,
      module_title: moduleTitle,
      intro: lesson.content.intro,
      sections_text: sectionsText,
    };
  }, [lesson, moduleTitle]);

  const runExplain = useCallback(async () => {
    setMode("explain");
    setLoading(true);
    setError(null);
    setExplain(null);
    try {
      const data = await postJSON("/explain", ctx);
      setExplain(data.markdown);
    } catch (e) {
      setError(e.message || "Something went wrong");
      toast({ title: "Couldn't fetch explanation", description: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  }, [ctx, toast]);

  const runDiagram = useCallback(async () => {
    setMode("diagram");
    setLoading(true);
    setError(null);
    setDiagram(null);
    try {
      const data = await postJSON("/diagram", ctx);
      setDiagram(data.diagram);
    } catch (e) {
      setError(e.message || "Something went wrong");
      toast({ title: "Couldn't generate diagram", description: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  }, [ctx, toast]);

  const close = () => {
    setMode(null);
    setError(null);
  };

  return (
    <>
      <div
        role="group"
        aria-label="AI assist"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginTop: 16,
        }}
      >
        <button
          type="button"
          onClick={runExplain}
          disabled={loading}
          className="btn btn-ghost"
          data-testid="ai-explain-btn"
          aria-label="Explain more with AI"
          style={{ minHeight: 44 }}
        >
          <Sparkles size={16} strokeWidth={1.75} aria-hidden="true" />
          Explain more
        </button>
        <button
          type="button"
          onClick={runDiagram}
          disabled={loading}
          className="btn btn-ghost"
          data-testid="ai-diagram-btn"
          aria-label="Generate a diagram with AI"
          style={{ minHeight: 44 }}
        >
          <Shapes size={16} strokeWidth={1.75} aria-hidden="true" />
          Diagram
        </button>
      </div>

      {mode === "explain" && (
        <Panel
          title={`Explain: ${lesson.title}`}
          onClose={close}
          onRetry={runExplain}
          loading={loading}
        >
          {loading && <Skeleton lines={7} />}
          {error && !loading && (
            <div
              data-testid="ai-panel-error"
              style={{
                padding: 14,
                border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)",
                borderRadius: 12,
                color: "var(--danger)",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}
          {explain && !loading && (
            <div data-testid="ai-explain-content">
              <ExplainBody text={explain} />
            </div>
          )}
        </Panel>
      )}

      {mode === "diagram" && (
        <Panel
          title={`Diagram: ${lesson.title}`}
          onClose={close}
          onRetry={runDiagram}
          loading={loading}
        >
          {loading && <Skeleton lines={5} />}
          {error && !loading && (
            <div
              data-testid="ai-panel-error"
              style={{
                padding: 14,
                border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)",
                borderRadius: 12,
                color: "var(--danger)",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}
          {diagram && !loading && (
            <div data-testid="ai-diagram-content">
              <div
                className="t-meta"
                style={{ color: "var(--muted)", marginBottom: 8 }}
              >
                Type: {diagram.type}
              </div>
              <DiagramRenderer diagram={diagram} />
            </div>
          )}
        </Panel>
      )}
    </>
  );
}
